import React, { useEffect, useState } from 'react'
import type { Toast as ToastType } from '../../contexts/ToastContext'

interface ToastProps {
  toast: ToastType
  onClose: (id: string) => void
}

export const Toast: React.FC<ToastProps> = ({ toast, onClose }) => {
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    // Trigger slide-in animation
    requestAnimationFrame(() => {
      setIsVisible(true)
    })
  }, [])

  const handleClose = () => {
    setIsVisible(false)
    // Wait for animation to complete before removing
    setTimeout(() => {
      onClose(toast.id)
    }, 300)
  }

  const typeConfig = {
    success: {
      bgColor: 'bg-mina-olive',
      textColor: 'text-white',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      ),
      ariaRole: 'status' as const,
    },
    error: {
      bgColor: 'bg-mina-chili',
      textColor: 'text-white',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      ),
      ariaRole: 'alert' as const,
    },
    warning: {
      bgColor: 'bg-mina-mustard',
      textColor: 'text-white',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      ),
      ariaRole: 'status' as const,
    },
    info: {
      bgColor: 'bg-mina-orange',
      textColor: 'text-mina-espresso',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      ariaRole: 'status' as const,
    },
  }

  const config = typeConfig[toast.type]

  return (
    <div
      role={config.ariaRole}
      aria-live={toast.type === 'error' ? 'assertive' : 'polite'}
      aria-atomic="true"
      className={`
        ${config.bgColor} ${config.textColor}
        rounded-mina shadow-mina p-4 mb-3 min-w-[320px] max-w-md
        flex items-start gap-3
        transition-all duration-300 ease-out
        ${isVisible ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'}
      `}
    >
      <div className="flex-shrink-0 mt-0.5">
        {config.icon}
      </div>

      <p className="flex-1 text-sm font-medium leading-relaxed">
        {toast.message}
      </p>

      <button
        onClick={handleClose}
        className={`
          flex-shrink-0 p-1 rounded-lg
          hover:bg-black/10 transition-colors
          focus:outline-none focus:ring-2 focus:ring-white/50
        `}
        aria-label="Close notification"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  )
}
